// DriverHolidayWarning — modal shown on the book-in pages when the
// selected driver has a holiday on the booking date. Mirrors the
// legacy book_in.php holiday-conflict warning. Non-blocking: staff
// can dismiss and proceed via "Continue Anyway".

import { AlertTriangle } from 'lucide-react';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';
import type { DriverHolidayItem } from '../../../hooks/useDriverHolidayCheck';
import { periodLabel } from '../../../lib/holidayPeriod';
import styles from './DriverHolidayWarning.module.css';

export interface DriverHolidayWarningProps {
  isOpen: boolean;
  onClose: () => void;
  /** "Continue Anyway" — proceed with this driver/date. */
  onContinueAnyway: () => void;
  /** "Change Driver/Date" — close and return focus to the driver field. */
  onChangeDriverOrDate: () => void;
  driverName: string;
  holidays: DriverHolidayItem[];
  /** YYYY-MM-DD — the booking date that triggered the warning. */
  bookingDate: string;
}

function formatDate(iso: string): string {
  const dt = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function DriverHolidayWarning({
  isOpen,
  onClose,
  onContinueAnyway,
  onChangeDriverOrDate,
  driverName,
  holidays,
  bookingDate,
}: DriverHolidayWarningProps): JSX.Element {
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Driver Holiday Warning"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onChangeDriverOrDate}>
            Change Driver/Date
          </Button>
          <Button variant="primary" onClick={onContinueAnyway}>
            Continue Anyway
          </Button>
        </>
      }
    >
      <div className={styles.heroRow}>
        <div className={styles.iconWrap} aria-hidden="true">
          <AlertTriangle size={28} />
        </div>
        <p className={styles.lead}>
          <strong>{driverName}</strong> is on holiday on {formatDate(bookingDate)}.
        </p>
      </div>
      {holidays.length > 0 ? (
        <ul className={styles.list}>
          {holidays.map((h) => (
            <li key={`${h.start_date}-${h.end_date}`} className={styles.listItem}>
              Holiday period: {formatDate(h.start_date)} to {formatDate(h.end_date)}
              {h.period !== 'FULL' ? ` (${periodLabel(h.period)})` : ''}
            </li>
          ))}
        </ul>
      ) : null}
      <p className={styles.tail}>Please select a different driver or date.</p>
    </Modal>
  );
}
